import { useState } from 'react';
import { fetchButtons, fetchButtonStats } from '../services/api';
import { CustomButton, ButtonStats } from '../types';
import { config } from '../config';
import { usePolling } from './usePolling';

/**
 * Custom hook for fetching and managing counter data including buttons and their statistics
 * 
 * This hook uses polling with the global interval to automatically fetch and refresh counter data,
 * combining both buttons and stats data into a single interface.
 * 
 * @param counterId - ID of the counter to fetch data for
 * @param isOwnerUrl - Whether the ID is an owner URL rather than a regular counter ID
 * @returns An object containing:
 *   - buttons: Array of button objects for this counter
 *   - stats: Array of button statistics
 *   - loading: Boolean indicating if initial data load is in progress
 *   - error: Error message if any loading errors occurred 
 *   - refreshing: Boolean indicating if a background refresh is in progress
 *   - lastUpdate: Timestamp of the last successful data update
 *   - fetchCounts: Function to manually trigger a refresh of button data
 *   - silentFetchCounts: Function to manually trigger a silent refresh of button data
 *   - fetchStats: Function to manually trigger a refresh of statistics data
 *   - silentFetchStats: Function to manually trigger a silent refresh of statistics data
 */
export function useCounterData(counterId: string, isOwnerUrl = false) {
  // Use our new usePolling hook for buttons data with silent updates
  const buttonPolling = usePolling<CustomButton[]>({
    fetchFn: async () => fetchButtons(counterId, isOwnerUrl),
    initialData: [],
    interval: config.polling.global,
    dependencies: [counterId, isOwnerUrl],
    silentUpdates: true // Enable silent updates to prevent UI flickering
  });

  // Use usePolling for stats data with silent updates
  const statsPolling = usePolling<ButtonStats[]>({
    fetchFn: async () => fetchButtonStats(counterId, isOwnerUrl),
    initialData: [],
    interval: config.polling.global,
    dependencies: [counterId, isOwnerUrl],
    silentUpdates: true // Enable silent updates to prevent UI flickering
  });

  return {
    buttons: buttonPolling.data || [],
    stats: statsPolling.data || [],
    loading: buttonPolling.loading,
    error: buttonPolling.error || statsPolling.error,
    refreshing: buttonPolling.refreshing || statsPolling.refreshing,
    lastUpdate: buttonPolling.lastUpdate,
    fetchCounts: buttonPolling.refetch,
    silentFetchCounts: buttonPolling.silentRefetch,
    fetchStats: statsPolling.refetch,
    silentFetchStats: statsPolling.silentRefetch
  };
} 